import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { GigyaService } from "../../services/gigya.service";
import { OrganizationPickerService } from "../../services/organization-picker.service";
import { defineCustomElement as defineNesButtonElement } from "@nessie/webcomponents/dist/components/nes-button";
import { InputComponent } from "../../shared/input/input.component";
import { ButtonComponent } from "../../shared/button/button.component";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { WarningIconComponent } from "../../shared/icons/warning/warning.component";
import { CheckCircleIconComponent } from "../../shared/icons/check-circle/check-circle.component";
import { TitleUnderlineIconComponent } from "../../shared/icons/title-underline/title-underline.component";
import { NotifService } from "../../services/notif.service";
import { Version, VersionService } from "../../services/version.service";
import { CredentialHandoffService } from "../../services/credential-handoff.service";
import { ActivationNoticeService } from "../../services/activation-notice.service";
import { CaptchaMode, CaptchaSolution, RecaptchaService } from "../../services/recaptcha.service";

defineNesButtonElement();

/**
 * CDC / Gigya CAPTCHA wiring (Google reCAPTCHA, triggered by CDC's Risk-Based
 * Authentication / progressive-CAPTCHA policy after failed logins).
 *
 * Flow: first accounts.login has no token -> CDC answers captchaNeeded -> we get a
 * v3 token (silent, score-based; CDC applies its score threshold) and retry with it
 * -> if CDC rejects that token, fall back once to a v2 (invisible, may show a
 * challenge) token. Without environment.recaptchaV3SiteKey it's v2 only.
 *
 * Confirmed against the test tenant (logintestzakelijk):
 *   - CAPTCHA required:  { errorCode: 401020, errorFlags: "captchaNeeded" }
 *     errorFlags is the primary signal - 401020 on its own is ambiguous (it is
 *     also "API rate limit exceeded" when the flag is absent).
 *   - Garbage token:     { errorCode: 401021 "Login Failed Wrong Captcha",
 *     errorFlags: "captchaNeeded" } - note the flag is present here too.
 *   - Valid token + wrong password: 403042, no errorFlags.
 *
 * CDC expects captchaType "reCaptchaV3" for v3 tokens, with reCAPTCHA v2 as the
 * fallback challenge (CAPTCHA_TYPE_BY_MODE).
 *
 * Still to CONFIRM against a real tenant with v3 configured:
 *   - What CDC returns for a below-threshold v3 score (assumed: the same
 *     401021/captchaNeeded as a bad token - that is what triggers the v2 fallback).
 *   - CAPTCHA_TOKEN_PARAM / CAPTCHA_TYPE_PARAM: "captchaToken" + "captchaType"
 *     per SAP KBA 3626162 ("...CAPTCHA Parameters Without Using Screen-Sets").
 *   - CAPTCHA_FAILED_ERROR_CODES: 400021 "Captcha verification failed" (KBA
 *     3627057); 401021 "Login Failed Wrong Captcha" (KBA 3598174).
 */
const CAPTCHA_TOKEN_PARAM = "captchaToken";
const CAPTCHA_TYPE_PARAM = "captchaType";
const CAPTCHA_TYPE_BY_MODE: Record<CaptchaMode, string> = {
  v3: "reCaptchaV3",
  v2: "reCaptchaV2",
};
const CAPTCHA_NEEDED_FLAG = "captchaNeeded";
const CAPTCHA_REQUIRED_ERROR_CODES: number[] = [401020];
const CAPTCHA_FAILED_ERROR_CODES: number[] = [400021, 401021];

@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    SpinnerComponent,
    LayoutComponent,
    WarningIconComponent,
    CheckCircleIconComponent,
    TitleUnderlineIconComponent,
  ],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LoginComponent implements OnInit {
  passwdInputType: "text" | "password" = "password";
  error: string | null = null;
  loading = false;
  showCaptchaChallenge = false;
  @ViewChild("captchaContainer") captchaContainer?: ElementRef<HTMLElement>;
  showPasswordField = false;
  availableMethods: string[] = [];
  verificationCodeSent: string | null = null;
  accountActivated = false;
  variant = this.versionService.current;
  layoutType: "blue" | "white" | "yellow" = this.versionService.current === Version.B2C ? 'yellow' : 'blue';
  titleKey = `${this.variant}.title`
  modernAuthFlow = this.versionService.modernAuthFlow

  constructor(
    private router: Router,
    private gigyaService: GigyaService,
    private organizationPicker: OrganizationPickerService,
    private translateService: TranslateService,
    private notifService: NotifService,
    private route: ActivatedRoute,
    public versionService: VersionService,
    private credentialHandoff: CredentialHandoffService,
    private activationNotice: ActivationNoticeService,
    private recaptcha: RecaptchaService,
    private cdr: ChangeDetectorRef,
  ) {
    switch (this.versionService.current) {
      case Version.B2B:
        this.layoutType = 'blue';
        break;
      case Version.B2C:
        this.layoutType = 'yellow';
        break;
      case Version.NSGO:
        this.layoutType = 'white';
        break;
      default:
        console.warn("LoginComponent - unknown version, defaulting to blue layout. version:", this.versionService.current);
        this.layoutType = 'blue';
    }
    console.info("LoginComponent constructed - version:", this.versionService.current, "| layoutType:", this.layoutType, "| modernAuthFlow:", this.modernAuthFlow);
  }

  form: FormGroup = new FormGroup({
    email: new FormControl(""),
    password: new FormControl(""),
  });

  public async ngOnInit(): Promise<void> {
    console.info("LoginComponent.ngOnInit - URL:", window.location.href);
    const stagedCredentials = this.credentialHandoff.consume();
    if (stagedCredentials) {
      console.info("LoginComponent.ngOnInit - staged credentials found (e.g. from onboarding-password), auto-submitting for email:", stagedCredentials.email);
      this.form.patchValue(stagedCredentials);
      this.showPasswordField = true;
      this.onSubmit();
      return;
    }
    console.info("LoginComponent.ngOnInit - no staged credentials, continuing with normal init flow");

    if (this.gigyaService.validContext()) {
      console.info("LoginComponent.ngOnInit - valid context found in URL, checking existing session.");
      this.showActivationNotice();
      let sessionValid = await this.checkSession();
      if (!sessionValid) {
        console.info("LoginComponent.ngOnInit - session not valid on first check, waiting 1500ms and retrying once (magic-link race condition guard).");
        // After a magic-link auto-login, Gigya may still be creating the
        // session when we check it. Wait briefly and retry once before
        // treating it as a real failure.
        await this.delay(1500);
        sessionValid = await this.checkSession();
      }
      console.info("LoginComponent.ngOnInit - final sessionValid state:", sessionValid);
      if (sessionValid === false && this.versionService.current === Version.NSGO) {
        console.warn("LoginComponent.ngOnInit - valid context but no session after retry (NSGO) - falling back to SSO trigger.");
        this.triggerSso();
      } else if (sessionValid === false) {
        console.warn("LoginComponent.ngOnInit - valid context but no session after retry, and version is not NSGO - user is left on the login form with no automatic recovery. This may present as a stuck/blank login on mobile.");
      }
    } else {
      console.warn("LoginComponent.ngOnInit - no valid context found in URL - trying to recover via fallback.");
      // Just activated via an invitation: stay here so the customer sees the banner and can log
      // in right away. After login, /continue takes them to the portal through the fallback.
      if (this.showActivationNotice()) {
        console.info("LoginComponent.ngOnInit - account just activated, staying on the login page without context.");
        return;
      }
      this.preventInvalidContext();
      if (this.versionService.current === Version.NSGO) {
        console.info("LoginComponent.ngOnInit - version is NSGO, also triggering SSO alongside the fallback.");
        this.triggerSso();
      }
    }
  }

  /** "Account activated" banner after the invite flow (see ActivationNoticeService). */
  private showActivationNotice(): boolean {
    const notice = this.activationNotice.consume();
    if (!notice) return false;
    this.accountActivated = true;
    if (notice.email && !notice.email.includes("*")) {
      this.form.patchValue({ email: notice.email });
    }
    return true;
  }

  public canRegister(): boolean {
    return this.versionService.current === Version.B2C;
  }

  public async onSubmit(): Promise<void> {
    console.info("LoginComponent.onSubmit - called. modernAuthFlow:", this.modernAuthFlow, "| showPasswordField:", this.showPasswordField);
    if (this.showCaptchaChallenge) {
      console.info("LoginComponent.onSubmit - ignored, waiting for the user to complete the reCAPTCHA checkbox.");
      return;
    }
    this.error = null;

    if (this.modernAuthFlow && !this.showPasswordField) {
      console.info("LoginComponent.onSubmit - modern auth flow, password field not yet shown - fetching auth methods instead of logging in.");
      this.getAuthMethods();
      return;
    }

    if (!this.form.valid) {
      console.warn("LoginComponent.onSubmit - form is invalid, aborting submit without calling gigya. email present:", !!this.form.controls["email"].value, "| password present:", !!this.form.controls["password"].value);
      return;
    }

    await this.attemptLogin();
  }

  /**
   * One accounts.login attempt. If CDC's RBA policy demands a CAPTCHA and we don't
   * have a token yet, solve reCAPTCHA (v3 first) and retry with the token; if CDC
   * rejects a v3 token, retry once more with a v2 token.
   */
  private async attemptLogin(captcha?: CaptchaSolution): Promise<void> {
    const params: Record<string, unknown> = {
      loginID: this.form.controls["email"].value,
      password: this.form.controls["password"].value,
      loginMode: "standard",
      ignoreInterruptions: true,
    };
    if (captcha) {
      params[CAPTCHA_TOKEN_PARAM] = captcha.token;
      params[CAPTCHA_TYPE_PARAM] = CAPTCHA_TYPE_BY_MODE[captcha.mode];
    }

    console.info("LoginComponent.attemptLogin - calling accounts.login", captcha ? `(with CAPTCHA ${captcha.mode} token)` : "(no CAPTCHA token)");
    this.loading = true;
    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.login, params);
    console.info("LoginComponent.attemptLogin - accounts.login result errorCode:", res.errorCode);

    if (res.errorCode === 0) {
      const { backTo, ...queryParams } = this.route.snapshot.queryParams;
      if (backTo) {
        console.info("LoginComponent.attemptLogin - login succeeded, backTo param present, navigating to:", backTo, "queryParams:", queryParams);
        this.router.navigate([backTo], { queryParams });
        return;
      }
      console.info("LoginComponent.attemptLogin - login succeeded, no backTo param, proceeding to organization picker resolution.");
      await this.organizationPicker.proceed();
      return;
    }

    const errorFlags = res["errorFlags"];
    // errorFlags is the authoritative signal - 401020 alone is ambiguous (it's also
    // "API rate limit exceeded" when the flag is absent), so only fall back to the
    // error-code check when CDC didn't send errorFlags at all.
    const captchaNeeded =
      typeof errorFlags === "string"
        ? errorFlags.includes(CAPTCHA_NEEDED_FLAG)
        : CAPTCHA_REQUIRED_ERROR_CODES.includes(res.errorCode);

    if (!captcha && captchaNeeded) {
      console.info("LoginComponent.attemptLogin - CDC requires a CAPTCHA (errorFlags:", errorFlags, "errorCode:", res.errorCode, "), solving reCAPTCHA then retrying.");
      return this.solveCaptchaThenRetry(this.recaptcha.firstMode);
    }

    const captchaRejected = captchaNeeded || CAPTCHA_FAILED_ERROR_CODES.includes(res.errorCode);
    if (captcha?.mode === "v3" && captchaRejected && this.recaptcha.canFallBackToV2) {
      console.info("LoginComponent.attemptLogin - CDC rejected the v3 token (errorCode:", res.errorCode, "), falling back to a v2 challenge.");
      return this.solveCaptchaThenRetry("v2");
    }

    console.warn("LoginComponent.attemptLogin - login failed with errorCode:", res.errorCode, "- delegating to handleLoginError.");
    this.loading = false;
    return this.handleLoginError(res);
  }

  private async solveCaptchaThenRetry(mode: CaptchaMode): Promise<void> {
    if (!this.recaptcha.enabled) {
      console.error("LoginComponent.solveCaptchaThenRetry - CDC demanded a CAPTCHA but no reCAPTCHA site key is set for this environment.");
      this.loading = false;
      this.error = this.translateService.instant("login.captchaUnavailable");
      return;
    }
    try {
      const solution = await this.solveCaptcha(mode);
      await this.attemptLogin(solution);
    } catch (err) {
      console.warn("LoginComponent.solveCaptchaThenRetry - reCAPTCHA", mode, "did not complete:", err);
      this.showCaptchaChallenge = false;
      this.loading = false;
      this.error = this.translateService.instant("login.captchaFailed");
    }
  }

  // v3 is silent; v2 is a visible checkbox the user has to tick, so show its
  // container (and drop the spinner) while we wait for them.
  private async solveCaptcha(mode: CaptchaMode): Promise<CaptchaSolution> {
    if (mode === "v3") {
      return this.recaptcha.solve("v3");
    }
    this.loading = false;
    this.showCaptchaChallenge = true;
    this.cdr.detectChanges();
    try {
      return await this.recaptcha.solve("v2", this.captchaContainer?.nativeElement);
    } finally {
      this.showCaptchaChallenge = false;
    }
  }

  private async handleLoginError(res: any): Promise<void> {
    console.info("LoginComponent.handleLoginError - errorCode:", res.errorCode);

    if (CAPTCHA_FAILED_ERROR_CODES.includes(res.errorCode)) {
      console.warn("LoginComponent.handleLoginError - CDC rejected the CAPTCHA token.");
      this.error = this.translateService.instant("login.captchaFailed");
      return;
    }

    switch (res.errorCode) {
      case 403101:
      case 403102:
        console.info("LoginComponent.handleLoginError - MFA required, navigating to /mfa.");
        this.router.navigate(['mfa'], { queryParamsHandling: "merge", queryParams: { "regToken": res.regToken } })
        break;
      case 206001: {
        console.info("LoginComponent.handleLoginError - registration incomplete, attempting finalizeRegistration.");
        const r = await this.gigyaService.execute(this.gigyaService.client.accounts.finalizeRegistration, {
          regToken: res.regToken,
        });
        console.info("LoginComponent.handleLoginError - finalizeRegistration result errorCode:", r.errorCode);
        if (r.errorCode === 0) {
          console.info("LoginComponent.handleLoginError - finalizeRegistration succeeded, retrying onSubmit.");
          return this.onSubmit();
        }
        if (r.errorCode === 206002) {
          console.info("LoginComponent.handleLoginError - finalizeRegistration needs verification, resending code.");
          const resend = await this.gigyaService.execute(this.gigyaService.client.accounts.resendVerificationCode, {
            regToken: res.regToken,
          });
          if (resend.errorCode === 0) {
            console.info("LoginComponent.handleLoginError - verification code resent successfully.");
            this.verificationCodeSent = this.translateService.instant(
              "login.verificationCodeSent",
            );
            return;
          }
          console.warn("LoginComponent.handleLoginError - resendVerificationCode failed, errorCode:", resend.errorCode);
          this.error = this.translateService.instant(
            "login.verificationCodeError",
          );
          return;
        }
        console.warn("LoginComponent.handleLoginError - finalizeRegistration failed with unexpected errorCode:", r.errorCode);
        this.error = this.translateService.instant(
          "login.finalizeRegistrationFailed",
        );
        break;
      }

      case 500001:
        console.warn("LoginComponent.handleLoginError - gigya server error (500001).");
        this.error = this.translateService.instant(
          "Server Error - try again later.",
        );
        break;

      case 206002: {
        console.info("LoginComponent.handleLoginError - verification required, resending code.");
        const r = await this.gigyaService.execute(this.gigyaService.client.accounts.resendVerificationCode, {
          regToken: res.regToken,
        });
        if (r.errorCode === 0) {
          console.info("LoginComponent.handleLoginError - verification code resent successfully.");
          this.verificationCodeSent = this.translateService.instant(
            "login.verificationCodeSent",
          );
          return;
        }
        console.warn("LoginComponent.handleLoginError - resendVerificationCode failed, errorCode:", r.errorCode);
        this.error = this.translateService.instant(
          "login.verificationCodeError",
        );
        break;
      }

      case 403100:
        console.info("LoginComponent.handleLoginError - password not set, navigating to /set-password.");
        this.router.navigate(["set-password"], { queryParamsHandling: "merge", queryParams: { "regToken": res.regToken } });
        break;

      default:
        console.warn("LoginComponent.handleLoginError - UNHANDLED errorCode, showing generic invalid-credentials message. errorCode:", res.errorCode, "full response:", res);
        this.error = this.translateService.instant(
          "login.invalidCredentials",
        );
    }
  }

  public selectAuthMethod(method: string): void {
    console.info("LoginComponent.selectAuthMethod - method:", method);
    if (method === "password") {
      console.info("LoginComponent.selectAuthMethod - showing password field.");
      this.form.addControl("password", new FormControl(""));
      this.showPasswordField = true;
      this.availableMethods = [];
      return;
    }

    const routeMap: Record<string, string> = {
      emailOtp: 'email-otp',
      magicLink: 'magic-link',
    };
    const route = routeMap[method] ?? method.toLowerCase();
    if (!routeMap[method]) {
      console.warn("LoginComponent.selectAuthMethod - method not found in routeMap, falling back to lowercased method name as route:", method, "->", route);
    }
    console.info("LoginComponent.selectAuthMethod - navigating to:", route);
    this.router.navigate([route], {
      queryParamsHandling: "preserve",
      queryParams: { email: this.form.controls["email"].value },
    });
  }

  private async getAuthMethods(): Promise<void> {
    const email = this.form.controls["email"].value;
    console.info("LoginComponent.getAuthMethods - fetching auth methods for entered email.");
    this.loading = true;

    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.auth.getMethods, {
      identifier: email,
    });

    this.loading = false;

    if (res.errorCode !== 0) {
      console.warn("LoginComponent.getAuthMethods - getMethods failed, errorCode:", res.errorCode);
      this.error = this.translateService.instant("login.invalidCredentials");
      return;
    }

    const methods: string[] = res['methods'] ?? [];
    console.info("LoginComponent.getAuthMethods - methods returned by gigya:", methods);

    if (methods.length === 0) {
      console.warn("LoginComponent.getAuthMethods - gigya returned zero methods for this identifier.");
      this.error = this.translateService.instant("login.invalidCredentials");
      return;
    }

    this.availableMethods = methods.filter((m) => m === "emailOtp");
    if (this.availableMethods.length === 0) {
      console.warn("LoginComponent.getAuthMethods - none of the returned methods matched supported UI options (only 'emailOtp' is handled here) - user may be stuck with no visible option. raw methods:", methods);
    } else {
      console.info("LoginComponent.getAuthMethods - availableMethods after filtering:", this.availableMethods);
    }
  }

  private async checkSession(): Promise<boolean> {
    const session = await this.gigyaService.execute(
      this.gigyaService.client.accounts.session.verify, {}
    )
    console.info(
      "LoginComponent.checkSession - session.verify result:", session.errorCode, session['errorMessage'],
      "| document.cookie:", document.cookie,
    );
    if (session.errorCode === 0) {
      console.info("LoginComponent.checkSession - session valid, proceeding to organization picker.");
      await this.organizationPicker.proceed();
      return true;
    }
    console.warn("LoginComponent.checkSession - session NOT valid. errorCode:", session.errorCode);
    return false;
  }

  private preventInvalidContext(): void {
    console.warn("LoginComponent.preventInvalidContext - invoking fallback safety net because context was missing/invalid.");
    this.versionService.redirectToFallback();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public triggerSso(): void {
    const params = new URLSearchParams(window.location.search);
    if (params.get("errorCode")) {
      console.warn("LoginComponent.triggerSso - 'errorCode' query param present, ABORTING SSO trigger to avoid a redirect loop. errorCode:", params.get("errorCode"));
      return;
    }
    console.info("LoginComponent.triggerSso - triggering SSO redirect for NSGO. redirectURL:", window.location.href);
    return this.gigyaService.client.accounts.socialLogin({
      provider: 'saml-nsgo1',
      redirectURL: window.location.href,
      authFlow: 'redirect'
    });
  }
}
